import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { listCasinos } from "@/data/casinos";
import { BrandLogo } from "./BrandLogo";
import {
  casinoReviewPath,
  guidePath,
  paymentPath,
  providerPath,
} from "@/lib/format";

type Column = {
  title: string;
  items: { label: string; href: string; hint?: string; icon?: string }[];
};

type Panel = {
  key: string;
  label: string;
  description: string;
  columns: Column[];
  /** Optional featured card on the right */
  featured?: {
    title: string;
    body: string;
    href: string;
    cta: string;
    brandSlug?: string;
    brandName?: string;
    brandLogoUrl?: string;
  };
};

export function MegaMenu({ orientation }: { orientation: "desktop" | "mobile" }) {
  const { t, prefix, lang } = useI18n();
  const isSv = lang === "sv";
  const path = (sv: string, en: string) => `${prefix}/${isSv ? sv : en}`;
  const [openKey, setOpenKey] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const featured = listCasinos({ priorityBrand: true })[0];

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpenKey(null);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpenKey(null);
    }
    if (openKey) {
      document.addEventListener("mousedown", onClick);
      document.addEventListener("keydown", onKey);
    }
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [openKey]);

  const panels: Panel[] = [
    {
      key: "casinos",
      label: t("nav.casinos"),
      description: isSv
        ? "Bläddra svenska och internationella casinon."
        : "Browse Swedish and international casinos.",
      columns: [
        {
          title: isSv ? "Toppkategorier" : "Top categories",
          items: [
            {
              label: isSv ? "Alla casinon" : "All casinos",
              href: `${prefix}/casino`,
              icon: "★",
              hint: isSv ? "Vår topplista" : "Our top list",
            },
            {
              label: isSv ? "Med svensk licens" : "With Swedish license",
              href: path(
                "casino-med-svensk-licens",
                "casinos-with-swedish-license",
              ),
              icon: "✓",
              hint: "Spelinspektionen",
            },
            {
              label: isSv ? "Utan svensk licens" : "Without Swedish license",
              href: path(
                "casino-utan-svensk-licens",
                "casinos-without-swedish-license",
              ),
              icon: "↗",
              hint: isSv ? "Större bonusar" : "Bigger bonuses",
            },
            {
              label: isSv ? "Utan Spelpaus" : "Without Spelpaus",
              href: path("casino-utan-spelpaus", "casinos-without-spelpaus"),
              icon: "△",
              hint: isSv ? "Risk varning" : "Risk warning",
            },
            {
              label: isSv ? "Nya casinon" : "New casinos",
              href: path("nya-casinon", "new-casinos"),
              icon: "✦",
              hint: "2026",
            },
            {
              label: isSv ? "Live casino" : "Live casino",
              href: `${prefix}/live-casino`,
              icon: "●",
              hint: isSv ? "Riktiga dealers" : "Real dealers",
            },
          ],
        },
        {
          title: isSv ? "Betalningar" : "By payment",
          items: [
            {
              label: isSv ? "Casino med Swish" : "Casinos with Swish",
              href: paymentPath(
                prefix,
                lang,
                isSv ? "swish-casino" : "swish-casinos",
              ),
              icon: "₷",
            },
            {
              label: "Trustly",
              href: paymentPath(prefix, lang, "trustly"),
              icon: "T",
            },
            {
              label: "BankID",
              href: paymentPath(prefix, lang, "bankid"),
              icon: "ID",
            },
            {
              label: "Zimpler",
              href: paymentPath(prefix, lang, "zimpler"),
              icon: "Z",
            },
            { label: "Brite", href: paymentPath(prefix, lang, "brite"), icon: "B" },
            {
              label: isSv ? "Krypto / Bitcoin" : "Crypto / Bitcoin",
              href: paymentPath(prefix, lang, "bitcoin"),
              icon: "₿",
            },
          ],
        },
        {
          title: isSv ? "Genvägar" : "Quick links",
          items: [
            {
              label: isSv ? "Bästa casino 2026" : "Best casinos 2026",
              href: path("basta-casino", "best-casinos"),
              icon: "🏆",
            },
            {
              label: isSv ? "Svartlistade" : "Under review",
              href: path("svartlistade-casinon", "blacklisted-casinos"),
              icon: "⚠",
            },
            {
              label: isSv ? "Casino utan BankID" : "Without BankID",
              href: path("casino-utan-bankid", "casinos-without-bankid"),
              icon: "—",
            },
            {
              label: isSv ? "Casino utan konto" : "Without account",
              href: path("casino-utan-konto", "casinos-without-account"),
              icon: "—",
            },
            {
              label: isSv ? "Sportbetting" : "Sportsbooks",
              href: `${prefix}/${isSv ? "spelbolag" : "sportsbooks"}`,
              icon: "⚽",
            },
            {
              label: isSv ? "Esport betting" : "Esports betting",
              href: `${prefix}/${isSv ? "esport-betting" : "esports-betting"}`,
              icon: "🎮",
            },
          ],
        },
      ],
      featured: featured
        ? {
            title: featured.name,
            body: isSv
              ? featured.bonus_summary_sv ?? "Utvald partner"
              : featured.bonus_summary_en ?? "Featured partner",
            href: casinoReviewPath(prefix, lang, featured.slug),
            cta: t("cta.readReview"),
            brandSlug: featured.slug,
            brandName: featured.name,
            brandLogoUrl: featured.logo_url,
          }
        : undefined,
    },
    {
      key: "bonuses",
      label: t("nav.bonuses"),
      description: isSv
        ? "Välkomstbonusar, free spins och kampanjer."
        : "Welcome bonuses, free spins and promotions.",
      columns: [
        {
          title: isSv ? "Bonus-typer" : "Bonus types",
          items: [
            {
              label: isSv ? "Casino bonus" : "Casino bonus",
              href: `${prefix}/casino-bonus`,
              icon: "🎁",
            },
            { label: "Free spins", href: `${prefix}/free-spins`, icon: "🎰" },
            {
              label: isSv ? "Bonus utan omsättningskrav" : "No-wagering bonus",
              href: `${prefix}/casino-bonus`,
              icon: "⚡",
            },
            {
              label: isSv ? "Insättningsfri bonus" : "No-deposit bonus",
              href: `${prefix}/casino-bonus`,
              icon: "○",
            },
            {
              label: isSv ? "Cashback" : "Cashback",
              href: `${prefix}/casino-bonus`,
              icon: "↺",
            },
          ],
        },
        {
          title: isSv ? "Lär dig villkoren" : "Learn the terms",
          items: [
            {
              label: isSv ? "Omsättningskrav" : "Wagering requirements",
              href: guidePath(
                prefix,
                lang,
                isSv ? "omsattningskrav" : "wagering-requirements",
              ),
              icon: "?",
            },
            {
              label: "RTP",
              href: guidePath(prefix, lang, "rtp"),
              icon: "%",
            },
            {
              label: isSv ? "Volatilitet" : "Volatility",
              href: guidePath(
                prefix,
                lang,
                isSv ? "volatilitet" : "volatility",
              ),
              icon: "↕",
            },
            {
              label: isSv ? "Bankroll management" : "Bankroll management",
              href: guidePath(prefix, lang, "bankroll-management"),
              icon: "$",
            },
          ],
        },
      ],
    },
    {
      key: "games",
      label: t("nav.slots"),
      description: isSv
        ? "Spelautomater, livecasino och speltillverkare."
        : "Slots, live casino and software providers.",
      columns: [
        {
          title: isSv ? "Spelutbud" : "Game library",
          items: [
            {
              label: isSv ? "Alla spelautomater" : "All slots",
              href: `${prefix}/${isSv ? "spelautomater" : "slots"}`,
              icon: "▣",
            },
            {
              label: "Live casino",
              href: `${prefix}/live-casino`,
              icon: "●",
            },
            {
              label: isSv ? "Bordsspel" : "Table games",
              href: guidePath(
                prefix,
                lang,
                isSv ? "blackjack-regler" : "blackjack-rules",
              ),
              icon: "♠",
            },
            {
              label: isSv ? "Roulette" : "Roulette",
              href: guidePath(
                prefix,
                lang,
                isSv ? "roulette-regler" : "roulette-rules",
              ),
              icon: "◎",
            },
            {
              label: isSv ? "Poker" : "Poker",
              href: guidePath(
                prefix,
                lang,
                isSv ? "poker-regler" : "poker-rules",
              ),
              icon: "♣",
            },
          ],
        },
        {
          title: isSv ? "Speltillverkare" : "Providers",
          items: [
            { label: "NetEnt", href: providerPath(prefix, lang, "netent") },
            {
              label: "Play'n GO",
              href: providerPath(prefix, lang, "playngo"),
            },
            {
              label: "Pragmatic Play",
              href: providerPath(prefix, lang, "pragmatic-play"),
            },
            {
              label: "Evolution",
              href: providerPath(prefix, lang, "evolution"),
            },
            {
              label: "Hacksaw Gaming",
              href: providerPath(prefix, lang, "hacksaw-gaming"),
            },
            {
              label: isSv ? "Alla speltillverkare" : "All providers",
              href: `${prefix}/${isSv ? "speltillverkare" : "software-providers"}`,
            },
          ],
        },
      ],
    },
    {
      key: "guides",
      label: t("nav.guides"),
      description: isSv
        ? "Guider, regler och spelbranschens termer."
        : "Guides, rules and gambling terminology.",
      columns: [
        {
          title: isSv ? "Casino-grunderna" : "Casino basics",
          items: [
            {
              label: isSv ? "Svensk spellicens" : "Swedish license",
              href: guidePath(
                prefix,
                lang,
                isSv ? "svensk-spellicens" : "swedish-gambling-license",
              ),
            },
            {
              label: isSv ? "Spelpaus.se" : "Spelpaus.se",
              href: guidePath(prefix, lang, "spelpaus"),
            },
            {
              label: isSv ? "Spelinspektionen" : "Spelinspektionen",
              href: path("spelinspektionen", "swedish-gambling-authority"),
            },
            {
              label: isSv ? "Stödlinjen" : "Stödlinjen",
              href: `${prefix}/stodlinjen`,
            },
          ],
        },
        {
          title: isSv ? "Spelregler" : "Game rules",
          items: [
            {
              label: "Blackjack",
              href: guidePath(
                prefix,
                lang,
                isSv ? "blackjack-regler" : "blackjack-rules",
              ),
            },
            {
              label: "Roulette",
              href: guidePath(
                prefix,
                lang,
                isSv ? "roulette-regler" : "roulette-rules",
              ),
            },
            {
              label: "Baccarat",
              href: guidePath(
                prefix,
                lang,
                isSv ? "baccarat-regler" : "baccarat-rules",
              ),
            },
            {
              label: "Poker",
              href: guidePath(
                prefix,
                lang,
                isSv ? "poker-regler" : "poker-rules",
              ),
            },
          ],
        },
        {
          title: isSv ? "Esport betting" : "Esports betting",
          items: [
            {
              label: "CS2 betting",
              href: `${prefix}/${isSv ? "esport-betting" : "esports-betting"}/cs2`,
            },
            {
              label: "Dota 2 betting",
              href: `${prefix}/${isSv ? "esport-betting" : "esports-betting"}/dota2`,
            },
            {
              label: "Valorant betting",
              href: `${prefix}/${isSv ? "esport-betting" : "esports-betting"}/valorant`,
            },
            {
              label: isSv ? "Alla esport-titlar" : "All esports titles",
              href: `${prefix}/${isSv ? "esport-betting" : "esports-betting"}`,
            },
          ],
        },
      ],
    },
  ];

  if (orientation === "mobile") {
    return (
      <div className="space-y-3">
        {panels.map((p) => (
          <details
            key={p.key}
            className="group rounded-xl border border-ink-100 bg-white"
          >
            <summary className="flex cursor-pointer list-none items-center justify-between rounded-xl px-4 py-3 text-sm font-semibold text-ink-900 transition group-open:bg-ink-50">
              {p.label}
              <span aria-hidden className="text-ink-400 transition group-open:rotate-180">
                ▾
              </span>
            </summary>
            <div className="space-y-4 px-4 pb-4 pt-2">
              {p.columns.map((col) => (
                <div key={col.title}>
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                    {col.title}
                  </p>
                  <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                    {col.items.map((item) => (
                      <li key={item.href}>
                        <Link
                          to={item.href}
                          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-ink-800 no-underline hover:bg-brand-50 hover:text-brand-800"
                        >
                          {item.icon && (
                            <span className="grid h-6 w-6 place-items-center rounded-md bg-brand-50 text-xs font-bold text-brand-800">
                              {item.icon}
                            </span>
                          )}
                          <span>{item.label}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>
    );
  }

  return (
    <nav
      ref={containerRef}
      className="hidden items-center gap-0.5 lg:flex"
      aria-label="Primary navigation"
    >
      {panels.map((panel) => {
        const isOpen = openKey === panel.key;
        return (
          <div
            key={panel.key}
            className="relative"
            onMouseEnter={() => setOpenKey(panel.key)}
            onMouseLeave={() => setOpenKey((k) => (k === panel.key ? null : k))}
          >
            <button
              type="button"
              onClick={() => setOpenKey(isOpen ? null : panel.key)}
              aria-expanded={isOpen}
              aria-haspopup="true"
              className={`flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition ${
                isOpen
                  ? "bg-brand-50 text-brand-800"
                  : "text-ink-700 hover:bg-ink-100 hover:text-ink-900"
              }`}
            >
              {panel.label}
              <span
                aria-hidden
                className={`text-[10px] transition ${isOpen ? "rotate-180" : ""}`}
              >
                ▾
              </span>
            </button>
            {isOpen && (
              <div
                className="absolute left-1/2 top-full z-40 w-[min(96vw,860px)] -translate-x-1/2 pt-2"
              >
                <div className="overflow-hidden rounded-2xl border border-ink-100 bg-white shadow-card-hover ring-1 ring-black/5 animate-fade-in-up">
                  <div className="grid grid-cols-12 gap-0">
                    <div
                      className={`p-6 ${
                        panel.featured ? "col-span-8" : "col-span-12"
                      } grid grid-cols-${Math.min(panel.columns.length, 3)} gap-6`}
                    >
                      {panel.columns.map((col) => (
                        <div key={col.title}>
                          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                            {col.title}
                          </p>
                          <ul className="space-y-1">
                            {col.items.map((item) => (
                              <li key={item.href}>
                                <Link
                                  to={item.href}
                                  className="group/item flex items-start gap-2 rounded-lg px-2 py-1.5 text-sm text-ink-800 no-underline transition hover:bg-brand-50 hover:text-brand-800"
                                  onClick={() => setOpenKey(null)}
                                >
                                  {item.icon && (
                                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-brand-50 text-xs font-bold text-brand-800 transition group-hover/item:bg-brand-100">
                                      {item.icon}
                                    </span>
                                  )}
                                  <span>
                                    <span className="block font-medium">
                                      {item.label}
                                    </span>
                                    {item.hint && (
                                      <span className="block text-[11px] text-ink-500">
                                        {item.hint}
                                      </span>
                                    )}
                                  </span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>

                    {panel.featured && (
                      <div className="col-span-4 relative overflow-hidden bg-gradient-to-br from-brand-950 to-brand-800 p-6 text-white">
                        <div
                          className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gold-500/30 blur-3xl"
                          aria-hidden
                        />
                        <span className="pill-gold text-[10px]">
                          ★ {t("label.priorityBrand")}
                        </span>
                        <div className="relative mt-4 flex items-center gap-3">
                          {panel.featured.brandSlug && panel.featured.brandName && (
                            <BrandLogo
                              slug={panel.featured.brandSlug}
                              name={panel.featured.brandName}
                              src={panel.featured.brandLogoUrl}
                              size={48}
                            />
                          )}
                          <div>
                            <p className="text-base font-bold">
                              {panel.featured.title}
                            </p>
                            <p className="text-xs text-white/70">
                              {isSv ? "Utvald partner" : "Featured partner"}
                            </p>
                          </div>
                        </div>
                        <p className="relative mt-3 text-xs text-white/80 line-clamp-3">
                          {panel.featured.body}
                        </p>
                        <Link
                          to={panel.featured.href}
                          onClick={() => setOpenKey(null)}
                          className="relative mt-4 inline-flex w-full items-center justify-center rounded-lg bg-white/10 px-3 py-2 text-sm font-semibold text-white no-underline backdrop-blur transition hover:bg-white/20"
                        >
                          {panel.featured.cta} →
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <Link
        to={path("ansvarsfullt-spelande", "responsible-gambling")}
        className="rounded-lg px-3 py-2 text-sm font-medium text-ink-700 no-underline hover:bg-ink-100 hover:text-ink-900"
      >
        {t("nav.responsible")}
      </Link>
    </nav>
  );
}
